import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { PassUsageCalculationMode } from './reports/pass-usage-calculation-mode.js';

export class ReportSettings extends DataModel {
  static readonly TYPE_ID: string = 'f73b446876024af793a6fbc7b552ddba';
  static readonly SCHEMA_VERSION: number = 6;

  private constructor(
    public readonly historicMonumentTax: number | null,
    public readonly theaterTax: number | null,
    public readonly amountsDonatedForHumanitarianPurposes: number | null,
    public readonly entertainmentTax: number | null,
    public readonly showFeeOnReport: boolean,
    public readonly passUsageCalculationMode: PassUsageCalculationMode,
    public readonly hideExtraProducts: boolean,
    public readonly allowServerSideReports: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return ReportSettings.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(ReportSettings.SCHEMA_VERSION);
  }

  static empty(): ReportSettings {
    return new ReportSettings(
        null,
        null,
        null,
        null,
        false,
        PassUsageCalculationMode.VoucherValue,
        false,
        false,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): ReportSettings {
    return new ReportSettings(
        MapUtils.getDoubleOrNull(map, 'historicMonumentTax'),
        MapUtils.getDoubleOrNull(map, 'theaterTax'),
        MapUtils.getDoubleOrNull(map, 'amountsDonatedForHumanitarianPurposes'),
        MapUtils.getDoubleOrNull(map, 'entertainmentTax'),
        MapUtils.getBoolean(map, 'showFeeOnReport'),
        toEnumFromString(MapUtils.getString(map, 'passUsageCalculationMode'), PassUsageCalculationMode),
        MapUtils.getBoolean(map, 'hideExtraProducts'),
        MapUtils.getBoolean(map, 'allowServerSideReports'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'historicMonumentTax': this.historicMonumentTax,
      'theaterTax': this.theaterTax,
      'amountsDonatedForHumanitarianPurposes': this.amountsDonatedForHumanitarianPurposes,
      'entertainmentTax': this.entertainmentTax,
      'showFeeOnReport': this.showFeeOnReport,
      'passUsageCalculationMode': fromEnumAsString(this.passUsageCalculationMode),
      'hideExtraProducts': this.hideExtraProducts,
      'allowServerSideReports': this.allowServerSideReports,
    };
  }

  clone(): this {
    return new ReportSettings(
        this.historicMonumentTax,
        this.theaterTax,
        this.amountsDonatedForHumanitarianPurposes,
        this.entertainmentTax,
        this.showFeeOnReport,
        this.passUsageCalculationMode,
        this.hideExtraProducts,
        this.allowServerSideReports,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.historicMonumentTax === this.historicMonumentTax &&
      other.theaterTax === this.theaterTax &&
      other.amountsDonatedForHumanitarianPurposes === this.amountsDonatedForHumanitarianPurposes &&
      other.entertainmentTax === this.entertainmentTax &&
      other.showFeeOnReport === this.showFeeOnReport &&
      other.passUsageCalculationMode === this.passUsageCalculationMode &&
      other.hideExtraProducts === this.hideExtraProducts &&
      other.allowServerSideReports === this.allowServerSideReports;
  }
}

export class ReportSettingsBuilder extends DataModelBuilder<ReportSettings> {
  build(context: SerializationContext, map: SerializedData): ReportSettings {
    return ReportSettings.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(ReportSettings.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ReportSettings.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // Version 2:
    //  - parameter `showFeeOnReport` of type `bool` with default value `false`
    //    was introduced
    if(version < 2) {
      map['showFeeOnReport'] ??= false;
      version = 2;
    }

    // Version 3:
    //  - parameter `hidePassUsages` of type `bool` with default value `false`
    //    was introduced
    if(version < 3) {
      map['hidePassUsages'] ??= false;
      version = 3;
    }

    // Version 4:
    //  - parameter `hideExtraProducts` of type `bool` with default value
    //    `false` was introduced
    if(version < 4) {
      map['hideExtraProducts'] ??= false;
      version = 4;
    }

    // Version 5:
    //  - parameter `allowServerSideReports` of type `bool` with default
    //    value `false` was introduced
    if(version < 5) {
      map['allowServerSideReports'] ??= false;
      version = 5;
    }

    /** Version 6:
     *  - parameter `hidePassUsages` of type `bool` was replaced with parameter
     *    `passUsageCalculationMode` of type `PassUsageCalculationMode`
     *  - forward migration:
     *    - `false` > `PassUsageCalculationMode.VoucherValue`
     *    - `true` > `PassUsageCalculationMode.Hidden`
     */
    if(version < 6) {
      const hidePassUsages = MapUtils.getBoolean(map, 'hidePassUsages');

      map['passUsageCalculationMode'] ??= fromEnumAsString(hidePassUsages ?
        PassUsageCalculationMode.Hidden :
        PassUsageCalculationMode.VoucherValue
      );

      version = 6;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: ReportSettings,
      map: SerializedData,
  ): SerializedData {
    let version = model.$schemaVersion.to ?? ReportSettings.SCHEMA_VERSION;

    /** Version 6:
     *  - parameter `hidePassUsages` of type `bool` was replaced with parameter
     *    `passUsageCalculationMode` of type `PassUsageCalculationMode`
     *  - backward migration:
     *    - PassUsageCalculationMode.VoucherValue => `false`,
     *    - PassUsageCalculationMode.ZeroPrice => `false`
     *      (not backward compatible => use default value),
     *    - PassUsageCalculationMode.Hidden => `true`,
     */
    if(version >= 6) {
      let hidePassUsages: boolean;
      switch(model.passUsageCalculationMode) {
        case PassUsageCalculationMode.VoucherValue:
          hidePassUsages = false;
          break;
        case PassUsageCalculationMode.ZeroPrice:
          hidePassUsages = false;
          break;
        case PassUsageCalculationMode.Hidden:
          hidePassUsages = true;
          break;
      }

      map['hidePassUsages'] = hidePassUsages;

      map['$schema_version'] = VersionRange.encodedBetween(1, 6);
      version = 1;
    }

    return map;
  }
}
